import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import { FC, useMemo } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AuditHistoryDto, AuditPropertyChangeDto } from '../../../dtos';
import { useGetOrderHistoryQuery } from '../../../store/generated/generatedApi';
import { formatDate } from '../../../util';

interface Props {
    open: boolean;
    orderId: string;
    onClose: () => void;
}

export const OrderHistoryDialog: FC<Props> = ({ open, orderId, onClose }) => {
    const { data: history, isLoading, isFetching } = useGetOrderHistoryQuery(
        { id: orderId },
        { skip: !open || !orderId }
    );

    const filteredHistory = useMemo(() => {
        if (!history) return [];
        return history.filter(entry => 
            entry.propertyChanges && entry.propertyChanges.length > 0
        );
    }, [history]);

    const hasHistory = useMemo(() => {
        return filteredHistory && filteredHistory.length > 0;
    }, [filteredHistory]);

    const getChangeTypeColor = (changeType: string) => {
        switch (changeType) {
            case 'Created':
                return 'success';
            case 'Modified':
                return 'info';
            case 'Deleted':
                return 'error';
            case 'Cancelled':
                return 'warning';
            case 'Restored':
                return 'secondary';
            default:
                return 'default';
        }
    };

    const formatEntityLabel = (entry: AuditHistoryDto) => {
        if (entry.entityType === 'OrderLineItem') {
            const lineItemNumber = entry.orderLineItemNumber
                ? ` #${entry.orderLineItemNumber}`
                : '';
            return `Order Line Item${lineItemNumber}`;
        }
        return 'Order';
    };

    const formatPropertyChange = (change: AuditPropertyChangeDto) => {
        const oldValue = change.oldValue || '(empty)';
        const newValue = change.newValue || '(empty)';
        return `${oldValue} → ${newValue}`;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Order History</DialogTitle>
            <DialogContent>
                {isLoading || isFetching ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                    </Box>
                ) : !hasHistory ? (
                    <Box mt={2}>
                        <Typography>No change history available for this order.</Typography>
                    </Box>
                ) : (
                    <Box mt={2}>
                        <List
                            sx={{
                                maxHeight: 600,
                                overflowY: 'auto',
                                border: '1px solid lightgray',
                                borderRadius: 2,
                                bgcolor: 'background.paper'
                            }}
                        >
                            {filteredHistory.map((entry, index) => (
                                <Box key={index}>
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={2}
                                                width="100%"
                                            >
                                                <Typography variant="body2" color="textSecondary">
                                                    {formatDate(entry.changeDate, 'MMM dd, yyyy h:mm:ss a')}
                                                </Typography>
                                                <Chip
                                                    label={entry.changeType}
                                                    color={getChangeTypeColor(
                                                        entry.changeType
                                                    ) as any}
                                                    size="small"
                                                />
                                                <Typography variant="body2" fontWeight="bold">
                                                    {formatEntityLabel(entry)}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{ ml: 'auto' }}
                                                >
                                                    by {entry.changedByDisplayName}
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {entry.propertyChanges &&
                                            entry.propertyChanges.length > 0 ? (
                                                <List dense>
                                                    {entry.propertyChanges.map(
                                                        (propertyChange, propIndex) => (
                                                            <ListItem key={propIndex}>
                                                                <ListItemText
                                                                    primary={
                                                                        propertyChange.propertyDisplayName ||
                                                                        propertyChange.propertyName
                                                                    }
                                                                    secondary={formatPropertyChange(
                                                                        propertyChange
                                                                    )}
                                                                />
                                                            </ListItem>
                                                        )
                                                    )}
                                                </List>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary">
                                                    No property changes recorded.
                                                </Typography>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                    {index < filteredHistory.length - 1 && (
                                        <Divider />
                                    )}
                                </Box>
                            ))}
                        </List>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

